import { Link, useLocation } from 'react-router';
import { Menu, X, Users, Settings, ShieldCheck, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { cn } from '../../lib/utils';

interface MobileHeaderProps {
  isMenuOpen: boolean;
  onMenuToggle: () => void;
}

const adminNavItems = [
  { name: 'Users', href: '/admin/users', icon: Users },
  { name: 'Audit Logs', href: '/admin/audit', icon: ShieldCheck },
  { name: 'Settings', href: '/admin/settings', icon: Settings },
];

export function MobileHeader({ isMenuOpen, onMenuToggle }: MobileHeaderProps) {
  const location = useLocation();
  const { user, tenant, logout } = useAuth();
  const primaryColor = tenant?.branding_primary_color || '#2563eb';

  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(href + '/');

  return (
    <>
      {/* Header bar */}
      <header className="h-14 border-b border-gray-200 bg-white px-4 flex items-center justify-between lg:hidden">
        <Link to="/dashboard" className="flex items-center">
          {tenant?.branding_logo_url ? (
            <img
              src={tenant.branding_logo_url}
              alt={tenant.branding_company_name || 'Logo'}
              className="h-8 w-auto"
            />
          ) : (
            <span
              className="text-lg font-bold"
              style={{ color: primaryColor }}
            >
              {tenant?.branding_company_name || 'SecureSend'}
            </span>
          )}
        </Link>
        <button
          onClick={onMenuToggle}
          className="p-2 -mr-2 text-gray-600 hover:text-gray-900"
          aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
        >
          {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </header>

      {/* Slide-out menu overlay */}
      {isMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={onMenuToggle}
          />

          {/* Menu panel */}
          <div className="fixed top-0 right-0 bottom-0 w-72 bg-white z-50 shadow-xl lg:hidden overflow-y-auto">
            {/* Menu header */}
            <div className="h-14 border-b border-gray-200 px-4 flex items-center justify-between">
              <span className="font-semibold text-gray-900">Menu</span>
              <button
                onClick={onMenuToggle}
                className="p-2 -mr-2 text-gray-600 hover:text-gray-900"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* User info */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-full font-semibold"
                  style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}
                >
                  {user?.name.charAt(0).toUpperCase()}
                </div>
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-700 truncate">{user?.name}</p>
                  <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                </div>
              </div>
            </div>

            {/* Admin navigation */}
            {user?.role === 'admin' && (
              <div className="p-4 border-b border-gray-200">
                <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-3">
                  Administration
                </p>
                <nav className="space-y-1">
                  {adminNavItems.map((item) => {
                    const active = isActive(item.href);
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={onMenuToggle}
                        className={cn(
                          'flex items-center rounded-md px-3 py-2 text-sm font-medium',
                          active
                            ? ''
                            : 'text-gray-700 hover:bg-gray-50'
                        )}
                        style={active ? { backgroundColor: `${primaryColor}15`, color: primaryColor } : undefined}
                      >
                        <item.icon
                          className="mr-3 h-5 w-5"
                          style={active ? { color: primaryColor } : { color: '#9ca3af' }}
                        />
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            )}

            {/* Logout */}
            <div className="p-4">
              <button
                onClick={() => {
                  onMenuToggle();
                  logout();
                }}
                className="flex items-center w-full rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                <LogOut className="mr-3 h-5 w-5 text-gray-400" />
                Sign out
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
